import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Button, TextField, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, Chip, Alert,
  CircularProgress, Stack, Badge,
} from '@mui/material';
import { Download as DownloadIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import { format, parseISO, subDays } from 'date-fns';
import { todayLocalISO } from '../../utils/dateUtils';
import { deaLogsApi, type DeaLogQuery, type AdministrationLogRecord } from '../../utils/api';
import type { DeaSchedule } from '../../types/treatmentSheet';
import ControlledSubstanceInventory from '../../components/DEALogs/ControlledSubstanceInventory';
import AttestAdministrationDialog from '../../components/DEALogs/AttestAdministrationDialog';
import { fmtQty } from '../../components/DEALogs/format';
import { useAuth } from '../../auth/useAuth';
import { useRBAC } from '../../auth/useRBAC';
import { inventoryControls } from '../../auth/pageControls';

const SCHEDULE_LABEL: Record<DeaSchedule, string> = {
  SCHEDULE_II: 'C-II', SCHEDULE_III: 'C-III', SCHEDULE_IV: 'C-IV', SCHEDULE_V: 'C-V',
};

// Human label for the clinical row kind that triggered an auto-logged entry.
const SOURCE_LABEL: Record<string, string> = {
  medication: 'medication charge-out',
  vaccination: 'vaccination',
  rxDispenseEvent: 'Rx dispense',
};

interface DEALogsPageProps {
  /** Prefill for the Add Substance dialog (the Inventory "Not DEA-tracked" chip). */
  enrolName?: string | null;
  onEnrolConsumed?: () => void;
}

const DEALogsPage: React.FC<DEALogsPageProps> = ({ enrolName, onEnrolConsumed }) => {
  const [tab, setTab] = useState(0);
  // An enrol request always lands on the Substances & Lots sub-tab.
  useEffect(() => {
    if (enrolName) setTab(0);
  }, [enrolName]);
  const [from, setFrom] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [to, setTo] = useState(todayLocalISO());
  const [schedule, setSchedule] = useState<DeaSchedule | ''>('');

  // Fetched at page level so the tab badge is live on every tab.
  const pendingQuery = useQuery(
    ['dea-pending-attestations'],
    () => deaLogsApi.listPendingAttestations(),
  );
  const pendingCount = pendingQuery.data?.length ?? 0;

  const query: DeaLogQuery = {
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(to + 'T23:59:59').toISOString() : undefined,
    schedule: schedule || undefined,
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" fontWeight={700}>DEA Controlled Substance Logs</Typography>
          <Chip
            label="Beta"
            size="small"
            sx={{
              height: 20, fontSize: '0.65rem', fontWeight: 600,
              bgcolor: '#C9772B', color: '#fff', '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Inspector-ready records per 21 CFR 1304. Records retained on file; not transmitted to DEA.
        </Typography>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Substances & Lots" />
          <Tab label="Administrations" />
          <Tab label="Reconciliation" />
          <Tab
            label={
              <Badge badgeContent={pendingCount} color="warning" max={99}>
                <Box sx={{ pr: pendingCount > 0 ? 1.5 : 0 }}>Needs Attestation</Box>
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <ControlledSubstanceInventory enrolName={enrolName} onEnrolConsumed={onEnrolConsumed} />
      )}
      {tab === 1 && (
        <AdministrationsTab
          from={from} to={to} schedule={schedule}
          onFromChange={setFrom} onToChange={setTo} onScheduleChange={setSchedule}
          query={query}
        />
      )}
      {tab === 2 && <ReconciliationTab />}
      {tab === 3 && (
        <PendingAttestationsTab
          logs={pendingQuery.data ?? []}
          isLoading={pendingQuery.isLoading}
          isError={pendingQuery.isError}
        />
      )}
    </Box>
  );
};

interface AdminTabProps {
  from: string; to: string; schedule: DeaSchedule | '';
  onFromChange: (s: string) => void;
  onToChange: (s: string) => void;
  onScheduleChange: (s: DeaSchedule | '') => void;
  query: DeaLogQuery;
}

const AdministrationsTab: React.FC<AdminTabProps> = ({
  from, to, schedule, onFromChange, onToChange, onScheduleChange, query,
}) => {
  const { data, isLoading, isError } = useQuery(
    ['dea-administrations', query],
    () => deaLogsApi.listAdministrations(query),
    { keepPreviousData: true },
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }} alignItems="flex-end">
        <TextField label="From" type="date" size="small"
          value={from} onChange={(e) => onFromChange(e.target.value)}
          InputLabelProps={{ shrink: true }} />
        <TextField label="To" type="date" size="small"
          value={to} onChange={(e) => onToChange(e.target.value)}
          InputLabelProps={{ shrink: true }} />
        <TextField select label="Schedule" size="small" value={schedule}
          onChange={(e) => onScheduleChange(e.target.value as DeaSchedule | '')}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="SCHEDULE_II">C-II</MenuItem>
          <MenuItem value="SCHEDULE_III">C-III</MenuItem>
          <MenuItem value="SCHEDULE_IV">C-IV</MenuItem>
          <MenuItem value="SCHEDULE_V">C-V</MenuItem>
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          startIcon={<PdfIcon />} variant="outlined"
          onClick={() => deaLogsApi.downloadPdf(query).catch((e) => alert(`PDF export failed: ${e?.response?.data?.message ?? e?.message ?? 'unknown error'}`))}
        >
          Export PDF
        </Button>
        <Button
          startIcon={<DownloadIcon />} variant="outlined"
          onClick={() => deaLogsApi.downloadCsv(query).catch((e) => alert(`CSV export failed: ${e?.response?.data?.message ?? e?.message ?? 'unknown error'}`))}
        >
          Export CSV
        </Button>
      </Stack>

      {isError && <Alert severity="error">Failed to load administrations.</Alert>}
      {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}

      {data && (
        <>
          <SectionTable
            title="Schedule II"
            subtitle="Maintained separately per 21 CFR 1304.04(h)(1)."
            logs={data.scheduleII}
          />
          <Box sx={{ mt: 4 }}>
            <SectionTable
              title="Schedules III, IV, V"
              subtitle="Maintained separately from Schedule II per 21 CFR 1304.04(h)(2)."
              logs={data.scheduleIIIthroughV}
            />
          </Box>
        </>
      )}
    </Paper>
  );
};

const SectionTable: React.FC<{ title: string; subtitle: string; logs: AdministrationLogRecord[] }> = ({
  title, subtitle, logs,
}) => (
  <Box>
    <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>{title}</Typography>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
      {subtitle} · {logs.length} record{logs.length === 1 ? '' : 's'}
    </Typography>
    {logs.length === 0 ? (
      <Alert severity="info">No administrations in this date range.</Alert>
    ) : (
      <Box sx={{ overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date / Time</TableCell>
              <TableCell>Drug</TableCell>
              <TableCell>Sched</TableCell>
              <TableCell>Lot</TableCell>
              <TableCell align="right">Qty Adm</TableCell>
              <TableCell align="right">Wasted</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>By</TableCell>
              <TableCell>Witness</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell>
                  {log.administeredAt ? format(parseISO(log.administeredAt), 'yyyy-MM-dd HH:mm') : '—'}
                  {log.autoLogged && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Auto-logged · {SOURCE_LABEL[log.sourceType ?? ''] ?? 'dispense'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <strong>{log.substance?.genericName ?? log.orderName}</strong>
                  {log.substance?.strength && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {log.substance.strength}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {log.substance?.deaSchedule && (
                    <Chip label={SCHEDULE_LABEL[log.substance.deaSchedule]} size="small" />
                  )}
                </TableCell>
                <TableCell>
                  {log.lot?.lotNumber ?? '—'}
                  {log.lot?.expirationDate && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      exp {format(parseISO(log.lot.expirationDate), 'MMM yyyy')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {log.quantityAdministered != null ? `${log.quantityAdministered} ${log.substance?.unit ?? ''}` : '—'}
                </TableCell>
                <TableCell align="right">
                  {/* A stored 0 is a real value (the server coerces omitted waste to 0 and the
                      PDF prints "0 mL") — only a genuinely absent value gets the dash, so this
                      column reads the same as the Reconciliation tab and the exports. */}
                  {log.quantityWasted == null ? '—' : Number(log.quantityWasted) > 0 ? (
                    <Chip
                      label={`${fmtQty(log.quantityWasted, log.substance?.unit)}${log.wasteReason ? ` · ${log.wasteReason.replace(/_/g, ' ').toLowerCase()}` : ''}`}
                      size="small" color="warning"
                    />
                  ) : (
                    <Typography variant="body2" component="span" color="text.secondary">
                      {fmtQty(0, log.substance?.unit)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {log.patient?.name ?? '—'}
                  {log.patient?.microchipId && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {log.patient.microchipId}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{log.administeredBy?.name ?? '—'}</TableCell>
                <TableCell>
                  {log.pending ? (
                    <Chip label="Pending attestation" size="small" color="warning" />
                  ) : (
                    log.witness?.name ?? '—'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )}
  </Box>
);

/**
 * Auto-logged dispenses awaiting vet+witness sign-off (ADR-0014), plus the
 * fail-loud "unmatched activity" list — controlled dispenses the auto-log
 * could NOT record (ambiguous registry match, insufficient lot balance).
 * Online-only like all DEA views; errors surface, nothing queues offline.
 */
const PendingAttestationsTab: React.FC<{
  logs: AdministrationLogRecord[];
  isLoading: boolean;
  isError: boolean;
}> = ({ logs, isLoading, isError }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // dea-logs.controller.ts `attest` :137-138 declares manage:MEDICAL_RECORDS, not
  // `manage:controlled_substances` - that slug exists in the vocabulary and no route asks it, so
  // gating on the plausible-sounding noun would hide Attest from people the API serves (INV-108).
  const rbac = useRBAC();
  const can = inventoryControls(rbac);
  const [attesting, setAttesting] = useState<AdministrationLogRecord | null>(null);

  const unmatchedQuery = useQuery(
    ['dea-unmatched-activity'],
    () => deaLogsApi.getUnmatchedActivity(),
  );

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (isError) return <Alert severity="error">Failed to load pending attestations.</Alert>;

  const refresh = () => {
    queryClient.invalidateQueries(['dea-pending-attestations']);
    queryClient.invalidateQueries(['dea-administrations']);
    queryClient.invalidateQueries(['dea-reconciliation']);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>Needs Attestation</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Controlled-substance dispenses recorded automatically at charge-out. The lot balance was
        already decremented; a witness sign-off completes the record. Signed records are immutable —
        incorrect entries are corrected by DEA amendment (no erasures).
      </Typography>

      {logs.length === 0 ? (
        <Alert severity="success">Nothing awaiting attestation.</Alert>
      ) : (
        <Box sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date / Time</TableCell>
                <TableCell>Drug</TableCell>
                <TableCell>Sched</TableCell>
                <TableCell>Lot</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Dispensed By</TableCell>
                <TableCell>Source</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{log.administeredAt ? format(parseISO(log.administeredAt), 'yyyy-MM-dd HH:mm') : '—'}</TableCell>
                  <TableCell>
                    <strong>{log.substance?.genericName ?? '—'}</strong>
                    {log.substance?.strength && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {log.substance.strength}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {log.substance?.deaSchedule && (
                      <Chip label={SCHEDULE_LABEL[log.substance.deaSchedule]} size="small" />
                    )}
                  </TableCell>
                  <TableCell>{log.lot?.lotNumber ?? '—'}</TableCell>
                  <TableCell align="right">
                    {log.quantityAdministered != null ? `${log.quantityAdministered} ${log.substance?.unit ?? ''}` : '—'}
                  </TableCell>
                  <TableCell>{log.patient?.name ?? '—'}</TableCell>
                  <TableCell>{log.administeredBy?.name ?? '—'}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {SOURCE_LABEL[log.sourceType ?? ''] ?? 'dispense'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {can.deaWrite && (
                      <Button size="small" variant="contained" onClick={() => setAttesting(log)}>
                        Attest
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {(unmatchedQuery.data?.length ?? 0) > 0 && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Controlled-substance activity that could NOT be auto-logged — no single registry match
            or no lot with sufficient balance. Record these manually and review lot balances.
          </Alert>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date / Time</TableCell>
                <TableCell>Dispensed As</TableCell>
                <TableCell>Registry Match</TableCell>
                <TableCell>Sched</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unmatchedQuery.data!.map((row) => (
                <TableRow key={row.movementId} hover>
                  <TableCell>{format(parseISO(row.occurredAt), 'yyyy-MM-dd HH:mm')}</TableCell>
                  <TableCell><strong>{row.name}</strong></TableCell>
                  <TableCell>{row.substance.genericName}</TableCell>
                  <TableCell>
                    <Chip label={SCHEDULE_LABEL[row.substance.deaSchedule]} size="small" />
                  </TableCell>
                  <TableCell align="right">{row.quantity} {row.substance.unit}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {SOURCE_LABEL[row.sourceType ?? ''] ?? row.sourceType ?? '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {attesting && (
        <AttestAdministrationDialog
          open
          log={attesting}
          currentUserId={user?.uid ?? ''}
          onClose={() => setAttesting(null)}
          onSuccess={refresh}
        />
      )}
    </Paper>
  );
};

const ReconciliationTab: React.FC = () => {
  const { data, isLoading, isError } = useQuery(
    ['dea-reconciliation'],
    () => deaLogsApi.getReconciliation(),
  );

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (isError) return <Alert severity="error">Failed to load reconciliation.</Alert>;
  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No controlled-substance lots recorded for this clinic yet.</Typography>
      </Paper>
    );
  }

  const discrepancyCount = data.filter((r) => r.hasDiscrepancy).length;

  return (
    <Paper sx={{ p: 2 }}>
      {discrepancyCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {discrepancyCount} lot{discrepancyCount === 1 ? '' : 's'} with quantity discrepancies. Investigate before next inspection.
        </Alert>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Substance</TableCell>
            <TableCell>Sched</TableCell>
            <TableCell>Lot</TableCell>
            <TableCell>Exp</TableCell>
            <TableCell align="right">Received</TableCell>
            <TableCell align="right">Administered</TableCell>
            <TableCell align="right">Wasted</TableCell>
            <TableCell align="right">Returned</TableCell>
            <TableCell align="right">Expected</TableCell>
            <TableCell align="right">Stored</TableCell>
            <TableCell align="right">Δ</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => (
            <TableRow key={row.lotId} hover sx={row.hasDiscrepancy ? { bgcolor: 'warning.50' } : {}}>
              <TableCell>
                <strong>{row.substance.genericName}</strong>
                {row.substance.brandName && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {row.substance.brandName} · {row.substance.strength}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip label={SCHEDULE_LABEL[row.substance.deaSchedule]} size="small" />
              </TableCell>
              <TableCell>{row.lotNumber}</TableCell>
              <TableCell>{format(parseISO(row.expirationDate), 'MMM yyyy')}</TableCell>
              {/* Every quantity column: fixed 3 decimals (the 0.001 discrepancy tolerance) +
                  the substance unit after a single space. "Returned" is already in the payload
                  and feeds Expected (= Received − Administered − Wasted + Returned), so without
                  it the row could not be eye-reconciled. */}
              <TableCell align="right">{fmtQty(row.quantityReceived, row.substance.unit, 3)}</TableCell>
              <TableCell align="right">{fmtQty(row.totalAdministered, row.substance.unit, 3)}</TableCell>
              <TableCell align="right">{fmtQty(row.totalWasted, row.substance.unit, 3)}</TableCell>
              <TableCell align="right">{fmtQty(row.totalReturned, row.substance.unit, 3)}</TableCell>
              <TableCell align="right">{fmtQty(row.expectedRemaining, row.substance.unit, 3)}</TableCell>
              <TableCell align="right">{fmtQty(row.storedRemaining, row.substance.unit, 3)}</TableCell>
              <TableCell align="right">
                {row.hasDiscrepancy ? (
                  <Chip label={row.discrepancy.toFixed(3)} size="small" color="error" />
                ) : '✓'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default DEALogsPage;
